import { Flex, notification } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { postCreateFromValidData } from "tm-shared/api";
import { Option } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useFormDependencies } from "@/hooks/useFormDependencies";
import {
    clearMarketCreatePageCache,
    setMarketCreatePageModalSelectionPayload,
    setMarketCreatePageOptionsCache,
    setNewMarketForm,
} from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";

import { useSyncRhfWithStore } from "../hooks";
import { FIELD_FLOW_MAP, type FieldName } from "../MarketCreate.constants";
import { AddressSection } from "./components/AddressSection";
import { LegalEntitySection } from "./components/LegalEntitySection";
import { LocationSection } from "./components/LocationSection";
import { MarketTypeSection } from "./components/MarketTypeSection";
import { PartnerSection } from "./components/PartnerSection";
import { WarningModal } from "./components/WarningModal";
import { StyledForm } from "./Styled";

const formId = "create-market-form";

interface CreateMarketFormProps {
    partnerOptions: Option[];
}

export const CreateMarketForm: FC<CreateMarketFormProps> = ({
    partnerOptions,
}) => {
    const [warningModalOpen, setWarningModalOpen] = useState(false);

    const handleCloseWarningModal = useCallback(() => {
        setWarningModalOpen(false);
    }, []);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { modalSelectionPayload, newMarketFormValues } = useAppSelector(
        (s) => s.ui.marketCreatePage,
    );

    const emptyValues: NewMarketFormValues = useMemo(
        () => ({
            partner: "",
            partnerExternalMarketId: "",
            region: "",
            storage: "",
            legalEntity: "",
            address: "",
            name: "",
            marketType: "",
            network: "",
            parentId: "",
        }),
        [],
    );

    const defaultValues: NewMarketFormValues = {
        ...emptyValues,
        ...newMarketFormValues,
    };

    const newMarketForm = useForm<NewMarketFormValues>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        resetField,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = newMarketForm;

    useSyncRhfWithStore(newMarketForm, setNewMarketForm);

    const onSubmit = useCallback(
        async (data: NewMarketFormValues) => {
            const {
                partnerExternalMarketId,
                parentId,
                address,
                legalEntity,
                name,
                marketType,
                storage,
                network,
                partner,
            } = data;

            if (!partnerExternalMarketId && !network && !warningModalOpen) {
                setWarningModalOpen(true);

                return;
            }

            try {
                await postCreateFromValidData({
                    partnerId: partner,
                    addressId: address,
                    contragentId: legalEntity,
                    type: marketType,

                    ...(name && { title: name }),
                    ...(partnerExternalMarketId && {
                        externalId: partnerExternalMarketId,
                    }),
                    ...(parentId && { parentId }),
                    ...(network && { networkId: network }),
                    ...(storage && { storageId: storage }),
                });

                reset(emptyValues);
                dispatch(clearMarketCreatePageCache());
                navigate(-1);
            } catch (error) {
                console.error(error);

                notification.error({
                    message: "Помилка при створенні маркету",
                });
            }
        },
        [dispatch, emptyValues, navigate, reset, warningModalOpen],
    );
    const dependencyManager = useFormDependencies(FIELD_FLOW_MAP);

    const resetBelow = useCallback(
        (fieldName: FieldName) => {
            const fieldsToClear = dependencyManager.getFieldsToReset(fieldName);

            fieldsToClear.forEach((name) => {
                resetField(name, { defaultValue: emptyValues[name] });
            });
        },
        [dependencyManager, emptyValues, resetField],
    );

    const watchedValues = useWatch({
        control,
    });

    const [isCheckingPartnerExternalId, setIsCheckingPartnerExternalId] =
        useState(false);
    const [isCheckingDuplicateAddress, setIsCheckingDuplicateAddress] =
        useState(false);

    const activeMap = useMemo(() => {
        return dependencyManager.computeActiveMap(watchedValues, {
            partnerExternalMarketId: (value) => {
                const okLength = Number(value?.length) >= 3;

                if (!okLength) return false;
                if (isCheckingPartnerExternalId) return false;
                if (errors.partnerExternalMarketId) return false;

                return true;
            },
            address: (value) => {
                if (isCheckingDuplicateAddress || !value) return false;

                return true;
            },
        });
    }, [
        dependencyManager,
        watchedValues,
        isCheckingPartnerExternalId,
        errors.partnerExternalMarketId,
        isCheckingDuplicateAddress,
    ]);

    useEffect(() => {
        if (modalSelectionPayload) {
            const { fieldName, option, value } = modalSelectionPayload;

            setValue(fieldName, value);

            if (option) {
                dispatch(
                    setMarketCreatePageOptionsCache({
                        fieldName,
                        options: [option],
                    }),
                );
            }

            dispatch(setMarketCreatePageModalSelectionPayload(null));
        }
    }, [dispatch, modalSelectionPayload, setValue]);

    return (
        <>
            <FormProvider {...newMarketForm}>
                <StyledForm
                    layout="vertical"
                    onFinish={handleSubmit(onSubmit)}
                    id={formId}
                >
                    <Flex vertical gap={16}>
                        <PartnerSection
                            activeMap={activeMap}
                            resetBelow={resetBelow}
                            setIsCheckingPartnerExternalId={
                                setIsCheckingPartnerExternalId
                            }
                            isCheckingPartnerExternalId={
                                isCheckingPartnerExternalId
                            }
                            partners={partnerOptions}
                        />

                        <LocationSection
                            activeMap={activeMap}
                            resetBelow={resetBelow}
                        />

                        <LegalEntitySection
                            activeMap={activeMap}
                            resetBelow={resetBelow}
                        />

                        <AddressSection
                            activeMap={activeMap}
                            resetBelow={resetBelow}
                            isChecking={isCheckingDuplicateAddress}
                            setIsChecking={setIsCheckingDuplicateAddress}
                        />

                        <MarketTypeSection
                            activeMap={activeMap}
                            resetBelow={resetBelow}
                        />
                    </Flex>

                    <Controller
                        name={"parentId"}
                        control={control}
                        render={() => <></>}
                    />

                    <Flex style={{ marginTop: "auto" }}>
                        <BaseButton
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={isSubmitting}
                            disabled={
                                !!errors.partnerExternalMarketId ||
                                isCheckingPartnerExternalId
                            }
                        >
                            Зберегти
                        </BaseButton>
                    </Flex>
                </StyledForm>
            </FormProvider>

            <WarningModal
                open={warningModalOpen}
                onClose={handleCloseWarningModal}
                formId={formId}
                loading={isSubmitting}
            />
        </>
    );
};
